import React, { useCallback, useContext, useState } from 'react';

import { useAppContext } from 'src/components/app-context';
import { ViewportContext } from 'src/components/viewport';
import { usePriceRowContext } from '../../../price-row-context';
import { useCorrectIconsInOfferListRowExperiment } from 'src/hooks/experiments';

import { Direction } from '@prisjakt/ui';
import { ButtonSize, ButtonType } from '@prisjakt/ui/button/variants';
import { DropdownContent, DropdownToggle } from '@prisjakt/ui/dropdown';
import { IconFlagOutlined, IconMenuKebab } from '@prisjakt/ui/icon';
import { MediumUp, MobileOnly } from 'src/ui/block';

import { IconProps } from '@prisjakt/ui/icon/types';
import { IOptionsColumn } from './types';

import * as RegularStyle from '../styles';
import { Dropdown, Option, Options, ReportOverlay } from './styles';
import ReportModal from 'src/components/report-modal';

const OptionsColumn: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IOptionsColumn>
>> = props => {
  const { alternativeOffer, className } = props;

  const { t } = useAppContext();
  const { isMobile } = useContext(ViewportContext);
  const {
    isFeatured,
    productImage,
    offer: { store },
  } = usePriceRowContext();
  const useCorrectIconsInOfferListRow = useCorrectIconsInOfferListRowExperiment();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const openModal = useCallback(
    (e: React.MouseEvent) => {
      // Stops the PriceRow link and onClick from firing
      e.preventDefault();
      e.stopPropagation();

      setModalIsOpen(true);
    },
    [setModalIsOpen]
  );

  const closeModal = useCallback(() => setModalIsOpen(false), [setModalIsOpen]);

  const color = isFeatured ? 'maybeBlue700' : 'base500';

  const getIcon = (iconProps: IconProps) =>
    useCorrectIconsInOfferListRow ? (
      <IconFlagOutlined {...iconProps} size={18} />
    ) : (
      <IconMenuKebab {...iconProps} />
    );

  return (
    <RegularStyle.Column className={className}>
      <ReportModal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        priceId={alternativeOffer.id}
        priceValue={alternativeOffer.price?.exclShipping}
        productName={alternativeOffer.name}
        productImage={productImage}
        stockStatus={alternativeOffer.stock?.status}
        isFeatured={isFeatured}
        store={store}
      />

      <Options>
        <Dropdown
          closeOnItemClick
          onToggle={(event: Event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
        >
          <DropdownToggle
            onlyIcon
            size={ButtonSize.MEDIUM}
            type={ButtonType.TEXT}
          >
            <MobileOnly>{getIcon({ color })}</MobileOnly>

            <MediumUp>{getIcon({ color })}</MediumUp>
          </DropdownToggle>

          <DropdownContent placement={isMobile ? Direction.Up : Direction.Down}>
            <ReportOverlay>
              <Option type="button" onClick={openModal}>
                {t('feedback', 'dialog.prompt-button')}
              </Option>
            </ReportOverlay>
          </DropdownContent>
        </Dropdown>
      </Options>
    </RegularStyle.Column>
  );
};

export default OptionsColumn;
